import { useEffect, useRef, useState } from "react";
import { X, Play, Pause, ChevronLeft, ChevronRight, Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { postNarrate } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { isRTL } from "@/lib/i18n";

const SLIDE_MS = 5000; // per photo when auto-advancing without audio

/**
 * MediaSlideshow — fullscreen Ken-Burns cycle through Wikipedia Commons photos
 * with an optional Radio Bulletin narration playing over the top.
 */
export default function MediaSlideshow({
  snapshotId,
  images = [],
  meta,
  t,
  language = "English",
  onClose,
}) {
  const rtl = isRTL(language);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const total = images.length;
  const current = images[index];

  // Auto-advance ticker (only when playing and no audio, otherwise sync to audio duration)
  useEffect(() => {
    if (!playing || total === 0) return;
    clearTimeout(timerRef.current);

    // If audio is playing, distribute slides evenly across its duration.
    const el = audioRef.current;
    if (audioUrl && el && !el.paused && el.duration && isFinite(el.duration)) {
      const perSlideMs = Math.max(2200, (el.duration * 1000) / total);
      timerRef.current = setTimeout(() => {
        setIndex((i) => (i + 1) % total);
      }, perSlideMs);
    } else {
      timerRef.current = setTimeout(() => {
        setIndex((i) => (i + 1) % total);
      }, SLIDE_MS);
    }

    return () => clearTimeout(timerRef.current);
  }, [index, playing, audioUrl, total]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % total);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + total) % total);
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, total]);

  const playBulletin = async () => {
    if (audioUrl && audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
      return;
    }
    setAudioLoading(true);
    trackEvent("slideshow_bulletin_play", { snapshot_id: snapshotId });
    try {
      const data = await postNarrate(snapshotId, "onyx");
      const src = `data:${data.mime || "audio/mpeg"};base64,${data.audio_base64}`;
      setAudioUrl(src);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = src;
          audioRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load bulletin");
    } finally {
      setAudioLoading(false);
    }
  };

  if (total === 0) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] bg-ink flex flex-col"
      dir={rtl ? "rtl" : "ltr"}
      onClick={(e) => e.stopPropagation()}
      data-testid="media-slideshow"
    >
      {/* Slide image with Ken-Burns effect */}
      <div className="flex-1 relative overflow-hidden bg-black">
        <img
          key={current.full_url + index /* re-mount so animation restarts */}
          src={current.thumb_url}
          alt={current.title}
          referrerPolicy="no-referrer"
          className="absolute inset-0 w-full h-full object-contain sm:object-cover tc-kenburns"
          data-testid={`slide-image-${index}`}
        />

        {/* Vignette / caption gradient */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

        {/* Caption */}
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 text-paper">
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-paper/70 mb-2">
            <span>{meta?.region?.flag} {meta?.region?.name}</span>
            <span>·</span>
            <span>{meta?.long_date}</span>
            <span>·</span>
            <span data-testid="slide-counter">{index + 1} / {total}</span>
          </div>
          <div className="font-headline text-xl sm:text-3xl font-bold leading-tight max-w-4xl">
            {current.title.replace(/\.(jpg|jpeg|png|tif|gif)$/i, "")}
          </div>
          {current.artist && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-paper/60">
              © {current.artist}{current.license ? ` · ${current.license}` : ""}
            </div>
          )}
        </div>

        {/* Prev / Next click zones */}
        <button
          aria-label="Previous"
          onClick={() => setIndex((i) => (i - 1 + total) % total)}
          className="absolute left-0 top-0 h-full w-1/4 sm:w-32 flex items-center justify-start pl-2 sm:pl-4 text-paper/40 hover:text-paper opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
          data-testid="slide-prev-btn"
        >
          <ChevronLeft size={40} />
        </button>
        <button
          aria-label="Next"
          onClick={() => setIndex((i) => (i + 1) % total)}
          className="absolute right-0 top-0 h-full w-1/4 sm:w-32 flex items-center justify-end pr-2 sm:pr-4 text-paper/40 hover:text-paper opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
          data-testid="slide-next-btn"
        >
          <ChevronRight size={40} />
        </button>
      </div>

      {/* Control bar */}
      <div className="bg-ink border-t-2 border-paper/30 text-paper p-3 sm:p-4 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setPlaying((p) => !p)}
          className="inline-flex items-center gap-2 border-2 border-paper px-3 py-2 font-mono text-[11px] uppercase tracking-widest bg-transparent hover:bg-paper hover:text-ink"
          data-testid="slide-play-btn"
        >
          {playing ? <Pause size={14} /> : <Play size={14} />}
          {playing ? (t?.pause || "Pause") : (t?.resume || "Play")}
        </button>

        <button
          onClick={playBulletin}
          disabled={audioLoading}
          className="inline-flex items-center gap-2 border-2 border-press-red bg-press-red text-paper px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-press-redHover disabled:opacity-60"
          data-testid="slide-bulletin-btn"
        >
          {audioLoading ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
          {audioLoading
            ? (t?.rolling || "Rolling…")
            : audioUrl
              ? (audioRef.current?.paused ? (t?.resume || "Resume") : (t?.pause || "Pause"))
              : (t?.playBulletin || "Play Bulletin")}
        </button>

        <div className="flex-1 min-w-0">
          <audio
            ref={audioRef}
            data-testid="slideshow-audio"
            onPlay={() => setPlaying(true)}
            controls={!!audioUrl}
            style={{ width: "100%", filter: "invert(1)", opacity: audioUrl ? 1 : 0 }}
          />
        </div>

        {/* Progress dots */}
        <div className="hidden sm:flex items-center gap-1" data-testid="slide-dots">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-2 h-2 border border-paper/60 ${i === index ? "bg-press-red border-press-red" : "bg-transparent"}`}
              data-testid={`slide-dot-${i}`}
            />
          ))}
        </div>

        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 border-2 border-paper/70 px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-paper hover:text-ink"
          data-testid="slide-close-btn"
        >
          <X size={14} /> {t?.close || "Close"}
        </button>
      </div>
    </div>
  );
}
